import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { ReferralPageBannerService } from '../home/referral-page-banner/referral-page-banner.service';
import { OeAppPersonService } from './oe-app-person.service';
import { OeAppService } from './oe-app.service';
import { ProfileServiceConfigurationService } from './profile-service-configuration.service';
import { TranslationService } from './translation.service';
import { Language } from './types/language';
import { PlayerViewerType } from './types/player-viewer-type.enum';
import { UtilsService } from './utils.service';
import { Program } from './types/program-type';
import { StudyBundleCourseDto } from './types/StudyBundleCourseDto';

@Injectable({
  providedIn: 'root',
})
export class LessonPlayerService {
  constructor(
    private readonly oeAppService: OeAppService,
    private readonly oeAppPersonService: OeAppPersonService,
    private readonly translationService: TranslationService,
    private readonly referralBannerService: ReferralPageBannerService,
    private readonly profileServiceConfigurationService: ProfileServiceConfigurationService
  ) {}

  getActivityUrl$(
    lessonId: string,
    viewerType: PlayerViewerType,
    isElligible: boolean,
    bookmark = 0,
    lessonType: Program['lessonType'] = 'MINI_LESSON',
    courseId?: number,
    isSkippingUnit = false
  ) {
    return this.profileServiceConfigurationService.aiLessonPlayerForMiniLessonEnabled$().pipe(
      take(1),
      switchMap((aiLessonPlayerForMiniLessonEnabled) =>
        this.buildActivityUrl$(
          lessonId,
          viewerType,
          isElligible,
          bookmark,
          lessonType,
          courseId,
          aiLessonPlayerForMiniLessonEnabled,
          isSkippingUnit
        )
      )
    );
  }

  getIscPlayerUrl$(course: StudyBundleCourseDto, bookmark = 0): Observable<string> {
    const url = UtilsService.createUrl(this.oeAppService.webIscPlayerHost, {
      ...this.buildCommonParams(course.mediaData, bookmark, course.id),
      environment: this.oeAppService.environment,
    });

    return of(url.href);
  }

  private buildActivityUrl$(
    lessonId: string,
    viewerType: PlayerViewerType,
    isElligible: boolean,
    bookmark: number,
    lessonType: Program['lessonType'],
    courseId: number | undefined,
    aiLessonPlayerForMiniLessonEnabled: boolean,
    isSkippingUnit = false
  ) {
    // render mini lessons (levels 1-8) in the AI lesson player when the kill switch
    // or the student's program-level offline mode flag is on
    const isMiniLessonInAiPlayer =
      (aiLessonPlayerForMiniLessonEnabled || this.oeAppPersonService.offlineModeEnabled) &&
      viewerType === PlayerViewerType.LESSON &&
      lessonType === 'MINI_LESSON';
    const useAiPlayer = lessonType === 'AI' || isMiniLessonInAiPlayer;

    const aiParams = {
      env: this.oeAppService.environment,
      personId: this.oeAppPersonService.id,
      lessonCopilotEnabled: this.oeAppPersonService.copilotEnabled,
      ...(isMiniLessonInAiPlayer ? { isMiniLesson: true } : {}),
    };

    const miniLessonParams = {
      environment: this.oeAppService.environment,
      viewerType,
      copilotEnabled: this.oeAppPersonService.copilotEnabled,
    };

    const playerUrl = useAiPlayer ? this.oeAppService.webLessonPlayerPageHost : this.oeAppService.activityPlayerHost();
    const urlParams = useAiPlayer ? aiParams : miniLessonParams;
    const url = UtilsService.createUrl(playerUrl, {
      ...this.buildCommonParams(lessonId, bookmark, courseId),
      ...urlParams,
      ...(isSkippingUnit ? { isSkippingUnit: true } : {}),
    });

    if (!isElligible) {
      url.setParam('referralMessageElligible', isElligible);
    } else {
      return this.referralBannerService.localizedBonusAmount$.pipe(
        switchMap((referralBonusAmount) => {
          if (referralBonusAmount) {
            url.setParam('referralBonusAmount', referralBonusAmount);
            url.setParam('referralMessageElligible', isElligible);
          } else {
            url.setParam('referralMessageElligible', 'false');
          }
          return of(url.href);
        }),
        catchError(() => {
          url.setParam('referralMessageElligible', 'false');
          return of(url.href);
        })
      );
    }

    return of(url.href);
  }

  // params every player host expects, regardless of which player renders the activity
  private buildCommonParams(lessonId: string, bookmark: number, courseId?: number) {
    const commonParams = {
      lessonId,
      language: this.translationService.getCurrentLanguage(),
      bookmark,
      isTranslationButtonEnabled: this.isTranslationButtonEnabled(),
      showTranslationButton: this.showTranslationButton(),
      areSoundEffectsEnabled: true,
      ...(courseId ? { courseId } : {}),
    };

    return commonParams;
  }

  private isTranslationButtonEnabled(): boolean {
    return this.oeAppPersonService.preferredLang !== Language.ENGLISH;
  }

  private showTranslationButton(): boolean {
    return this.oeAppPersonService.levelNum < 2;
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/lesson-player.service.ts